import { FC, useState } from 'react';
import { Popover } from 'antd';
import { formatPointsByLocale } from 'lowcomp/plugins/member/utils';
import styled from './index.module.scss';
import { useGlobal } from '@/utils/useGlobal';
import { copyText } from '@/components/Ambassador/util';
import { ICouponDetail, CouponType, UseStatus } from '@/components/Ambassador/index.d';
import { CouponIcon, IntegralIcon, GiftIcon, QuestionIcon, CopyIcon, IntArrowIcon } from '@/components/UI/svg/Referral';
import { SuccessIcon } from '@/components/UI/svg/Ecocredits';
import { useMemberTimeStore } from 'lowcode/utils/context/memberTimeZone';
import { getNotes } from '@/components/Ambassador/util';
import EfModal from '@/components/UI/Modal';
import Button from '@/components/UI/Button';

interface ICouponProps {
    coupon: ICouponDetail;
}

const Coupon: FC<ICouponProps> = ({ coupon }) => {
    const { useLang, useRouter, Link, isMobile } = useGlobal();
    const router = useRouter();
    const { locale } = router;
    const { t } = useLang('referral');
    const { t: sharedT } = useLang('shared');
    const [shopPop, setShopPop] = useState(false);
    const { timezoneList } = useMemberTimeStore();

    const useStateMap: Record<string, string> = {
        [UseStatus.EXPIRED]: 'EXPIRED',
        [UseStatus.USED]: 'USED',
    };

    const successBtnMap: Record<string, string> = {
        COUPONS: t('store'),
        THIRD_COUPONS: t('spend_now'),
    };

    const shopNow = () => {
        const { jumpUrl } = coupon.rules;
        if (coupon.prizeType === CouponType.THIRD_COUPONS) {
            if (isMobile) {
                router.push(jumpUrl);
                return;
            }
            window.open(jumpUrl, '_blank');
            return;
        }
        const href = jumpUrl;
        if (href) {
            if (href.startsWith('http')) {
                setTimeout(() => window.open(href, '_blank'));
            } else {
                window.location.href = href;
            }
            return;
        }

        if (isMobile) {
            router.push(`https://${locale}.ecoflow.com`);
            return;
        }
        setTimeout(() => {
            window.open(`https://${locale}.ecoflow.com`, '_blank');
        });
    };

    const handleShopPop = () => {
        if (coupon.useStatus !== UseStatus.UNUSED || coupon.prizeType === CouponType.CASH) return;
        copyText(coupon.coupon);
        setShopPop(true);
    };

    const getShowBtn = () => {
        if (coupon.prizeType !== CouponType.INTEGRAL) return true;

        return false;
    };

    const icon = () => {
        switch (coupon.prizeType) {
            case CouponType.INTEGRAL:
                return <IntegralIcon />;
            case CouponType.COUPONS:
                return <CouponIcon />;
            default:
                return <GiftIcon />;
        }
    };

    const rules = getNotes(coupon.rules, t, locale, timezoneList);

    const content = (
        <div className={styled.rules}>
            <h6 className="title">{t('rule2')}</h6>
            <ul>
                {rules.map((item: string) => (
                    <li key={item}>{item}</li>
                ))}
            </ul>
        </div>
    );

    const leftHtml = () => {
        switch (coupon.prizeType) {
            case CouponType.INTEGRAL:
                return (
                    <>
                        <div className="num">{formatPointsByLocale(coupon.points, locale)}</div>
                        <div className="code">{t('ecocredits')}</div>
                    </>
                );
            case CouponType.COUPONS:
                return (
                    <>
                        <div className="num">
                            <span>{coupon.rules?.amount || 0}</span>
                            <span className="off">
                                {coupon.rules.discountType === 'percentage' ? '%' : coupon?.rules.currencySymbol || '$'}
                                <br />
                                {t('coupon_off')}
                            </span>
                        </div>
                        {coupon.useStatus === UseStatus.UNUSED ? (
                            <div className="code">
                                {t('coupon_code')}:&nbsp;<span>{coupon.coupon}</span>
                                <CopyIcon
                                    onClick={(e: Element) => {
                                        e.stopPropagation();
                                        copyText(coupon.coupon, sharedT);
                                    }}
                                />
                            </div>
                        ) : (
                            <div className="status">{useStateMap[coupon.useStatus]}</div>
                        )}
                    </>
                );
            case CouponType.GIFT_CARD:
                return (
                    <>
                        <div className="num">
                            <span className="ccy">{coupon.rules?.currencySymbol || '$'}</span>
                            <span>{coupon.rules?.amount || 0}</span>
                        </div>
                        {coupon.useStatus === UseStatus.UNUSED ? (
                            <div className="code">
                                {t('coupon_code')}:&nbsp;<span>{coupon.coupon}</span>
                                <CopyIcon
                                    onClick={(e: Element) => {
                                        e.stopPropagation();
                                        copyText(coupon.coupon, sharedT);
                                    }}
                                />
                            </div>
                        ) : (
                            <div className="status">{useStateMap[coupon.useStatus]}</div>
                        )}
                    </>
                );
            default:
                return (
                    <>
                        <div className="num">
                            {/* <span className="ccy">{coupon.rules.currencySymbol}</span> */}
                            <span>{coupon.discountName || 0}</span>
                        </div>
                        {coupon.useStatus === UseStatus.UNUSED ? (
                            <div className="code">
                                {t('coupon_code')}:&nbsp;<span>{coupon.coupon}</span>
                                <CopyIcon
                                    onClick={(e: Element) => {
                                        e.stopPropagation();
                                        copyText(coupon.coupon, sharedT);
                                    }}
                                />
                            </div>
                        ) : (
                            <div className="status">{useStateMap[coupon.useStatus]}</div>
                        )}
                    </>
                );
        }
    };

    return (
        <>
            {coupon.prizeType !== CouponType.CASH ? (
                <div
                    className={`${styled.coupon} ${coupon.prizeType === CouponType.INTEGRAL ? 'integral' : ''} ${
                        coupon.prizeType === CouponType.GIFT_CARD || coupon.prizeType === CouponType.THIRD_COUPONS ? 'gift' : ''
                    } ${coupon.useStatus !== UseStatus.UNUSED && coupon.prizeType !== CouponType.INTEGRAL ? 'disabed' : ''}`}
                    onClick={handleShopPop}
                >
                    <div className="coupon_left">{leftHtml()}</div>
                    <div className="coupon_line"></div>
                    <div className="coupon_right">
                        <span className="icon">{icon()}</span>
                        {coupon.prizeType === CouponType.INTEGRAL ? (
                            <Link href={`/${locale}/ecocredits`}>
                                <p className="tools">
                                    {t('coupon_redeem')} <IntArrowIcon />
                                </p>
                            </Link>
                        ) : (
                            <p className="tools">
                                {t('rule2')}
                                <Popover overlayClassName={styled.rules_pop} content={content} trigger="click">
                                    <QuestionIcon
                                        onClick={(e: Element) => {
                                            e.stopPropagation();
                                        }}
                                    />
                                </Popover>
                            </p>
                        )}
                    </div>
                </div>
            ) : (
                <div className={`${styled.coupon} cash`}>
                    <div className="coupon_cash">
                        <div className="cash_container">
                            <div className="num">
                                <span className="ccy">{coupon.rules?.currencySymbol || '$'}</span>
                                <span>{coupon?.money || 0}</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <EfModal
                className={styled.couponModal}
                visible={shopPop}
                centered
                onCancel={() => {
                    setShopPop(false);
                }}
            >
                <div className="content">
                    <div className="m-icon">
                        <SuccessIcon />
                    </div>
                    <h5 className="m-title">{t('copy_desc')}</h5>

                    {getShowBtn() && (
                        <Button onClick={() => shopNow()} className="m-btn">
                            {successBtnMap[coupon.prizeType] || t('store')}
                        </Button>
                    )}
                </div>
            </EfModal>
        </>
    );
};

export default Coupon;
